import * as React from 'react';
import { connect } from 'react-redux';
import Form, { FormComponentProps } from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Radio from 'antd/lib/radio';
import message from 'antd/lib/message';
import Select from 'antd/lib/select';
import withStyles from 'react-jss';
import { StylesProps, KeyLabel } from 'src/Library/Types';
import { createAddress } from 'src/api/organisationProfiling';
import { renderInputField, renderSelectDropDown } from 'src/Library/Common';
import { LocationPreferences} from "../InquiryUtils";
import { useGenericState } from 'src/Library/UseGenericState';
import { searchCountry } from 'src/api/country';
import { useDebounce } from 'src/hooks/useDebounce';
import AddCustomCFSLocationModal from './AddCustomCFSLocationModal';
import { inchargeOptions, locationTypes } from '../../Organisation/consignorAddressConstants';
import { tokens } from 'src/design-system';

const styles = (theme) => ({
  newLocationModal: {
    '& .ant-modal-title': {
      fontSize: `${tokens.fontSize.h3}px`,
      color: '#444444',
      fontWeight: 'bold',
    },
    '& .ant-modal-body': {
      padding: '14px',
    },
  },
  rowDiv: {
    marginLeft: '14px',
    padding: 10,
    '& .ant-form-item': {
      margin: 0,
      padding: 0,
    },
    '& .ant-form-explain': {
      fontSize: tokens.fontSize.caption,
    },
  },
  title: {
    marginTop: '10px',
    fontSize: `${tokens.fontSize.small}px`,
    color: '#262626',
    fontWeight: tokens.fontWeight.semibold,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    fontSize: `${tokens.fontSize.h3}px`,
    margin: '16px 3%',
  },
  mainDiv: {
    backgroundColor: tokens.colors.bgWhite,
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  newUserDiv: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 6,
  },
  userRow: {
    marginBottom: 10,
    fontSize: tokens.fontSize.small,
  },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, FormComponentProps {
  visible: boolean;
  handleCancel: () => void;
  loadAddresses: () => void;
  locationPreferences;
  showLocationInput: boolean;
  consignorAddressTypes;
}

interface StateProps {
  isLocationInputRequired: boolean;
  loading: boolean;
  isSearchingCountry: boolean;
  searchTerm: string;
  countryList: KeyLabel [];
}

const AddLocationModal = (props: IProps) => {
  const {
    visible,
    handleCancel,
    form,
    classes,
    loadAddresses,
    locationPreferences,
    showLocationInput,
    consignorAddressTypes,
  } = props;

  const { getFieldValue, getFieldDecorator, validateFields, setFieldsValue } = form;

  const [state, setState] = useGenericState<StateProps>({
    isLocationInputRequired: showLocationInput,
    loading: false,
    countryList: [],
    isSearchingCountry: false,
    searchTerm: '',
  });

  const {
    isLocationInputRequired,
    loading,
    isSearchingCountry,
    searchTerm,
    countryList,
  } = state;

  const debouncedSearchCountry = useDebounce(searchTerm, 300);

  React.useEffect(() => {
    setState({ isSearchingCountry: true });
    searchCountryList(debouncedSearchCountry);
  }, [debouncedSearchCountry]);

  const searchCountryList = async (queryString: string = '') => {
    let countryList : KeyLabel [] = [];
    const response = await searchCountry({ queryString });
    if (response.isSuccess) {
      countryList = response.data.map(country => ({
        key: country.id,
        label: country.countryName,
      }))
    }
    setState({ countryList, isSearchingCountry: false });
  }

  const handleUpdate = async (values) => {
    setState({ loading: true });
    const apiObject = values;
    apiObject.location = values.location || 'FACTORY';
    apiObject.locationType = values.locationType || 'WAREHOUSE';
    apiObject.addressTypes = values.addressTypes || ['STUFFING', 'DESTUFFING'];
    if (getFieldValue('incharge') === 'existing') {
      apiObject.inchargeContactEmail = '';
      apiObject.inchargeContactName = '';
      apiObject.inchargeContactPhone = '';
    } else {
      apiObject.inchargeUserId = null;
    }
    const response = await createAddress(apiObject);
    if (response.isSuccess) {
      message.success(
        `Address created successfully`
      );
      loadAddresses();
      handleCancel()
    } else {
      message.error(response.errorMessage);
    }
    setState({ loading: false });
  };

  const handleSubmit = async (): Promise<void> => {
    validateFields(async (err, values) => {
      if (err) return;
      await handleUpdate(values);
    });
  };


  const renderInchargeField = () => {
    return (
      <Row className={classes.rowDiv} gutter={12}>
        <Col span={4} />
        <Col span={20} className={classes.newUserDiv}>
          <Row className={classes.userRow}>
            {renderInputField(form, 'inchargeContactName', 'Name', false, 8)}
          </Row>
          <Row className={classes.userRow}>
            {renderInputField(form, 'inchargeContactEmail', 'Email', false, 12)}
            {renderInputField(form, 'inchargeContactPhone', 'Phone', false, 8, 6)}
          </Row>
        </Col>
      </Row>
    );
  };

  const renderInchargeType = (): React.ReactElement => {
    return (
      <Col span={8}>
        <Form.Item>
          {getFieldDecorator('incharge')(<Radio.Group options={inchargeOptions} />)}
        </Form.Item>
      </Col>
    );
  };

  const renderLocationField = (): React.ReactElement => {
    return (
      <Row gutter={24} className={classes.rowDiv} style={{ display: 'flex', alignItems: 'center' }}>
        {showLocationInput && <Col span={12}>{renderLocationInput({ left: 8, right: 16 })}</Col>}
        <Col span={4} className={classes.title} style={{ marginTop: 0 }}>
          Location Type*
        </Col>
        <Col span={8} style={{ marginTop: 0 }}>
          <Form.Item>
            {getFieldDecorator('locationType', {
              rules: [{ required: true, message: 'Please select location type' }],
            })(<Radio.Group options={locationTypes} />)}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderName = (): React.ReactElement => {
    return (
      <Row gutter={24} className={classes.rowDiv}>
        <Col span={4} className={classes.title}>
          Name*
        </Col>
        {renderInputField(form, 'name', 'Type Organisation Name', true)}
      </Row>
    );
  };

  const renderAddressLandmark = (): React.ReactElement => {
    return (
      <Row gutter={24} className={classes.rowDiv}>
        <Col span={4} className={classes.title}>
          Address Details*
        </Col>
        {renderInputField(form, 'address', 'Enter Address', true)}
        {renderInputField(form, 'landmark', 'Enter Landmark')}
      </Row>
    );
  };

  const renderCityState = (): React.ReactElement => {
    return (
      <Row gutter={24} className={classes.rowDiv}>
        <Col span={4} className={classes.title}></Col>
        {renderInputField(form, 'pincode', 'PIN Code', false, 4)}
        {renderInputField(form, 'city', 'City', false, 6, 0)}
        {renderInputField(form, 'state', 'State', false, 6)}
        <Col span={4} style={{ paddingLeft: 0 }}>
          <Form.Item>
            {getFieldDecorator('countryId',
              {
                rules: [{ required: true, message: 'Required' }],
              })(
              <Select
                getPopupContainer={(trigger: any) => trigger.parentNode}
                showSearch
                placeholder="Country"
                onSearch={val => setState({ searchTerm: val })}
                onFocus={() => setState({ searchTerm: '' })}
                allowClear
                filterOption={(input, option) => true}
                dropdownMatchSelectWidth={false}
                dropdownRender={menu => renderSelectDropDown(menu, isSearchingCountry)}>
                {countryList?.map(item => (
                  <Select.Option key={item.key}>{item.label}</Select.Option>
                ))}
              </Select>)}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderIncharge = (): React.ReactElement => {
    return (
      <React.Fragment>
        <Row gutter={24} className={classes.rowDiv}>
          <Col span={4} className={classes.title}>
            Incharge Contact Details
          </Col>
          {renderInchargeType()}
        </Row>
        {renderInchargeField()}
      </React.Fragment>
    );
  };

  const renderAddresstypeField = (): React.ReactElement => {
    return (
      <Col span={8}>
        <Form.Item>
          {getFieldDecorator("addressTypes", {
            rules: [
              {
                required: true,
                message: "Required",
              },
            ],
          })(
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select address type"
            >
              {consignorAddressTypes?.map((item) => (
                <Select.Option key={item.key}>{item.label}</Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Col>
    );
  };

  const renderAddressTypes = (): React.ReactElement => {
    return (
      <Row gutter={24} className={classes.rowDiv}>
        <Col span={4} className={classes.title}>
          Address Type*{" "}
        </Col>
        {renderAddresstypeField()}
      </Row>
    );
  };

  const renderForm = () => {
    if (getFieldValue('location') === LocationPreferences.CFS) {
      return (
        <div>
          <div style={{ marginLeft: '10px' }}>
            {renderLocationInput({ left: 4, right: 8 }, { paddingLeft: '4px' })}
          </div>
          <AddCustomCFSLocationModal form={form} />
        </div>
      );
    }
    return (
      <Form className={classes.mainDiv}>
        {renderLocationField()}
        {renderAddressTypes()}
        {renderName()}
        {renderAddressLandmark()}
        {renderCityState()}
        {renderIncharge()}
      </Form>
    );
  };

  const renderLocationInput = (colSpan, styles?) => {
    return (
      <div className={classes.mainDiv}>
        <Row gutter={24} className={classes.rowDiv}>
          <Col span={colSpan.left} className={classes.title} style={{ paddingLeft: '4px' }}>
            Location*
          </Col>
          <Col span={colSpan.right} style={{ paddingLeft: '10px', paddingRight: '2px', ...styles }}>
            <Form.Item>
              {getFieldDecorator('location', {
                rules: [
                  {
                    required: true,
                    message: 'Required',
                  },
                ],
              })(
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select Location"
                  onChange={() => {
                    setState({ isLocationInputRequired: false })
                  }}
                >
                  {locationPreferences?.map((item) => (
                    <Select.Option key={item.key}>{item.label}</Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>
      </div>
    )
  }

  return (
    <Modal
      className={classes.newLocationModal}
      width={770}
      title="Add Location Details"
      visible={visible}
      style={{ padding: '0' }}
      onCancel={handleCancel}
      footer={[
        <a onClick={handleCancel} style={{ color: '#424242', marginRight: '16px' }}>
          Cancel
        </a>,
        <Button
          key="submit"
          type="primary"
          style={{
            background: tokens.colors.primary,
            border: tokens.colors.primary,
            color: tokens.colors.bgWhite,
          }}
          onClick={handleSubmit}
          loading={loading}
          disabled={isLocationInputRequired && !getFieldValue('location')}
        >
          Add
        </Button>,
      ]}
    >
      {isLocationInputRequired ?
        <div style={{ height: '400px' }}>{renderLocationInput({ left: 3, right: 8 })}</div>
        :
        renderForm()}
    </Modal>
  );
};

const mapStateToProps = ({ organisation, masterData }, {}) => ({
  locationPreferences: masterData.locationPreferences,
  consignorAddressTypes: masterData.consignorAddressTypes,
});

export default Form.create<any>()(
  connect(mapStateToProps)(withStyles(styles)(React.memo(AddLocationModal)))
);
